import { Box, useTheme } from "@mui/material";
import type { FC } from "react";
import { FONT_SIZE, INK, NEUTRAL, TARGET_GAP_METER } from "../../../../theme/tokens";
import MetaChip, { type MetaChipTone } from "../../Badges/MetaChip";
import { SystemLinearMeter } from "../SystemLinearMeter/SystemLinearMeter";

type Props = {
	/** What there is (scheduled hours). */
	value: number;
	/** What was needed (needed hours) — drawn as the tick the fill falls short of or passes. */
	target: number;
	/** Already translated and formatted, e.g. "Scheduled 212 h". Sits under the start of the bar. */
	valueLabel: string;
	/** Already translated and formatted, e.g. "Needed 230 h". Sits under the end of the bar. */
	targetLabel: string;
	/** The gap as a pill between the two ("18 h short"). `null` = no pill, when there is no gap worth naming. */
	gapLabel: string | null;
	/** DEFAULT: `warning` — a shortfall is the usual reason to draw one of these. */
	gapTone?: MetaChipTone;
	/** Already translated. The bar's accessible summary ("212 of 230 hours scheduled"). */
	ariaLabel: string;
	dataTestId?: string;
};

/**
 * How far a value is from its target: a brand fill against a target tick, the two figures underneath (the target's
 * ending at its tick) and the gap between them as a pill — "scheduled 212 h, needed 230 h, 18 h short". Built on `SystemLinearMeter`; the track runs a
 * little past the larger figure (`TARGET_GAP_METER.HEADROOM`) so the tick never sits on the end cap.
 */
export const TargetGapMeter: FC<Props> = ({ value, target, valueLabel, targetLabel, gapLabel, gapTone = "warning", ariaLabel, dataTestId }) => {
	const theme = useTheme();
	const max = Math.max(value, target) * TARGET_GAP_METER.HEADROOM;
	const targetRatio = max > 0 ? target / max : 0;
	return (
		<Box data-testid={dataTestId}>
			<SystemLinearMeter
				value={value}
				max={max}
				targetRatio={targetRatio}
				color={theme.palette.primary.main}
				trackColor={NEUTRAL[150]}
				targetColor={INK.SECONDARY}
				height={TARGET_GAP_METER.HEIGHT_PX}
				ariaLabel={ariaLabel}
			/>
			{/* The legend ends at the tick, not the track, so "Needed 230 h" sits under the line it names. */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1,
					mt: 0.75,
					paddingInlineEnd: `${(1 - targetRatio) * 100}%`,
					fontSize: FONT_SIZE.LABEL_SM,
					color: INK.MUTED,
				}}
			>
				<Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
					{valueLabel}
				</Box>
				{gapLabel !== null && <MetaChip size="small" tone={gapTone} label={gapLabel} dataTestId={dataTestId === undefined ? undefined : `${dataTestId}-gap`} />}
				<Box component="span" sx={{ fontVariantNumeric: "tabular-nums", textAlign: "end" }}>
					{targetLabel}
				</Box>
			</Box>
		</Box>
	);
};
